import { IBookingDates } from '@core/models/booking';
import { StringUtil } from './string';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

const DAYS_RANGE = 1;

/**
 * Convierte un objeto NgbDate a una cadena de fecha en formato "día-mes-año".
 * @param date - Objeto NgbDate o null.
 * @returns Cadena de fecha en formato "día-mes-año" o una cadena vacía si el valor es null.
 */
export const convertNgbDateToDate = (date: NgbDate | null): string => {
  if (date instanceof NgbDate) {
    return `${date.year}-${date.month}-${date.day}`;
  }
  return date || '';
};

/**
 * Convierte una cadena de fecha en formato "día-mes-año" a una cadena de fecha ISO.
 * @param date - Cadena de fecha en formato "día-mes-año".
 * @returns Cadena de fecha en formato ISO.
 */
export const convertFormatDate = ({ date, time }: { date: string; time: string }): string => {
  return `${date}T${time || '00:00:00'}.000`;
};

/**
 * Convierte una cadena de fecha en formato "día-mes-año" a un objeto NgbDate.
 * @param date - Cadena de fecha en formato "día-mes-año".
 * @returns Objeto NgbDate.
 */
export const convertDateToNgbDate = (date: string): NgbDate => {
  const [year, month, day] = date.split('-');
  return new NgbDate(+year, +month, +day);
};

export const convertDateToDateYear = (date: string): string => {
  const [day, month, year] = date.split('-');
  return `${year}-${month}-${day}`;
};

// Formatea la fecha según el lenguaje
export const getDateLanguage = (date: string): string => {
  const formatter = (date: string): any => {
    date = date.split(' ')[0];
    const [day, month, year] = date.split('-');
    return { day, month, year };
  };

  const { day, month, year } = formatter(date);
  return getDateFormatLanguage({ day, month, year });
};

export const getDateFormatLanguage = ({
  year,
  month,
  day,
  language = 'es',
}: {
  year: number;
  month: number;
  day: number;
  language?: string;
}): string => {
  const locale = language === 'es' ? 'es-ES' : 'en-US';
  const date = new Date(year, month - 1, day);
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return StringUtil.capitalizeEachWord(formatter.format(date));
};

/**
 * Calcula la diferencia en días entre dos fechas dadas en formato "año-mes-día".
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @returns Número de días de diferencia entre las dos fechas.
 */
export const getDiffDays = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 1;

  const [yearStart, monthStart, dayStart] = startDate.split('-').map(Number);
  const [yearEnd, monthEnd, dayEnd] = endDate.split('-').map(Number);
  const start = new Date(yearStart, monthStart - 1, dayStart);
  const end = new Date(yearEnd, monthEnd - 1, dayEnd);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calcula la diferencia en horas entre dos horas dadas en formato "hora:minutos:segundos".
 * @param startTime - Hora de inicio en formato "hora:minutos:segundos".
 * @param endTime - Hora de fin en formato "hora:minutos:segundos".
 * @returns Número de horas de diferencia entre las dos horas.
 */
export const getDiffHours = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 1;

  const [hoursStart, minutesStart] = startTime.split(':').map(Number);
  const [hoursEnd, minutesEnd] = endTime.split(':').map(Number);
  return hoursEnd - hoursStart + (minutesEnd - minutesStart) / 60;
};

export const getDiffHoursAndMinutes = (
  startTime: string,
  endTime: string
): { hours: number; minutes: number } => {
  if (!startTime || !endTime) return { hours: 0, minutes: 0 };

  const [hoursStart, minutesStart] = startTime.split(':').map(Number);
  const [hoursEnd, minutesEnd] = endTime.split(':').map(Number);
  return {
    hours: hoursEnd - hoursStart,
    minutes: minutesEnd - minutesStart,
  };
};

export const getDiffDates = ({
  startDate,
  endDate,
  startTime,
  endTime,
}: {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}): { days: number; hours: number; minutes: number } => {
  if (!startDate || !endDate || !startTime || !endTime) {
    return { days: 0, hours: 0, minutes: 0 };
  }

  const start = new Date(`${startDate}T${startTime}`);
  const end = new Date(`${endDate}T${endTime}`);

  console.log({ start, end });

  const diffMs = end.getTime() - start.getTime();

  const MS_BY_DAY = 1000 * 60 * 60 * 24;
  const MS_BY_HOUR = 1000 * 60 * 60;
  const MS_BY_MINUTE = 1000 * 60;
  // Calcular días completos
  const days = Math.floor(diffMs / MS_BY_DAY);
  // Calcular horas restantes
  const remainingHours = diffMs % MS_BY_DAY;
  const hours = Math.floor(remainingHours / MS_BY_HOUR);
  // Calcular minutos restantes
  const remainingMinutes = remainingHours % MS_BY_HOUR;
  const minutes = Math.floor(remainingMinutes / MS_BY_MINUTE);

  return {
    days,
    hours,
    minutes,
  };
};

/**
 * Parsea una cadena de fecha en formato "día-mes-año" a un objeto Date sin ajustes de zona horaria.
 * @param date - Cadena de fecha en formato "día-mes-año".
 * @returns Objeto Date.
 */
function parseDateAsLocal({ date, time }: { date: string; time?: string }): Date {
  console.log({ date, time });
  const [year, month, day] = date.split('-').map(Number);
  if (time) {
    const [hour, minutes, seconds] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minutes, seconds);
  } else {
    return new Date(year, month - 1, day, 0, 0, 0);
  }
}

// /**
//  * Parsea una cadena de fecha en formato ISO a un objeto Date ignorando la zona horaria.
//  * @param dateString - Cadena de fecha en formato ISO.
//  * @returns Objeto Date.
//  */
// function parseDateIgnoreTimezone(dateString: string): Date {
//   const [datePart] = dateString.split('T');
//   const [year, month, day] = datePart.split('-').map(Number);
//   return new Date(year, month - 1, day);
// }

/**
 * Parsea las fechas de entrada y salida y las fechas de rango de un checkoutLine.
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @param checkoutLine - Objeto que contiene las fechas de rango.
 * @returns Objeto con las fechas de entrada, salida, inicio de rango y fin de rango.
 */
const parseDates = ({
  startDate,
  endDate,
  checkoutLine,
}: {
  startDate: string;
  endDate: string;
  checkoutLine: any;
}): { entryDate: Date; exitDate: Date; rangeStart: Date; rangeEnd: Date } => ({
  entryDate: parseDateAsLocal({ date: startDate }),
  exitDate: parseDateAsLocal({ date: endDate }),
  rangeStart: parseDateAsLocal({ date: checkoutLine.startDate.split('T')[0] }),
  rangeEnd: parseDateAsLocal({ date: checkoutLine.endDate.split('T')[0] }),
});

const parseDatesWithHours = ({
  checkoutLine,
  startDate,
  startTime,
  endTime,
}: {
  checkoutLine: any;
  startDate: string;
  startTime: string;
  endTime: string;
}): { entryDate: Date; exitDate: Date; rangeStart: Date; rangeEnd: Date } => {
  const [startDateLine, startTimeLine] = checkoutLine.startDate.split('T');
  const [endDateLine, endTimeLine] = checkoutLine.endDate.split('T');
  return {
    entryDate: parseDateAsLocal({ date: startDate, time: startTime }),
    exitDate: parseDateAsLocal({ date: startDate, time: endTime }),
    rangeStart: parseDateAsLocal({
      date: startDateLine,
      time: startTimeLine.slice(0, 8),
    }),
    rangeEnd: parseDateAsLocal({
      date: endDateLine,
      time: endTimeLine.slice(0, 8),
    }),
  };
};

/**
 * Valida si hay intersección de fechas entre las fechas de entrada y salida y las fechas de rango de los checkoutLines.
 * @param checkoutLines - Array de objetos que contienen las fechas de rango.
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @returns Verdadero si hay intersección de fechas, falso en caso contrario.
 */
export const validateDateIntersection = ({
  checkoutLines,
  isServiceByDay,
  dates,
}: {
  checkoutLines: any[];
  isServiceByDay: boolean;
  dates: IBookingDates;
}): boolean => {
  const { startDate, endDate } = dates;
  return checkoutLines.some((checkoutLine) => {
    console.log({
      endDate: isServiceByDay ? startDate : endDate,
      isServiceByDay,
    });
    const { entryDate, exitDate, rangeStart, rangeEnd } = parseDates({
      checkoutLine,
      startDate,
      endDate: isServiceByDay ? startDate : endDate,
    });

    if (isDateEqual({ entryDate, exitDate, rangeStart, rangeEnd })) {
      return false;
    }

    return entryDate <= rangeEnd && exitDate >= rangeStart;
  });
};

/**
 * Valida si hay intersección de fechas entre las fechas de entrada y salida y las fechas de rango de los checkoutLines.
 * @param checkoutLines - Array de objetos que contienen las fechas de rango.
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @returns Verdadero si hay intersección de fechas, falso en caso contrario.
 */
export const validateHourIntersection = ({
  checkoutLines,
  dates,
}: {
  checkoutLines: any[];
  dates: IBookingDates;
}): boolean => {
  // console.log({ checkoutLines, dates });
  return checkoutLines.some((checkoutLine) => {
    const { entryDate, exitDate, rangeStart, rangeEnd } = parseDatesWithHours({
      checkoutLine,
      ...dates,
    });

    console.log({ entryDate, exitDate, rangeStart, rangeEnd });

    if (isDateEqual({ entryDate, exitDate, rangeStart, rangeEnd })) {
      return false;
    }

    return entryDate < rangeEnd && exitDate > rangeStart;
  });
};

/**
 * Valida si las fechas de entrada y salida son exactamente iguales a las fechas de rango de los checkoutLines.
 * @param checkoutLines - Array de objetos que contienen las fechas de rango.
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @returns Índice del checkoutLine que tiene fechas exactamente iguales, -1 si no hay coincidencias.
 */
export const validateDateEqual = ({
  checkoutLines,
  isServiceByDay,
  dates,
}: {
  checkoutLines: any[];
  isServiceByDay: boolean;
  dates: IBookingDates;
}): number => {
  const { startDate, endDate } = dates;
  return checkoutLines.findIndex((checkoutLine) => {
    const { entryDate, exitDate, rangeStart, rangeEnd } = parseDates({
      checkoutLine,
      startDate,
      endDate: isServiceByDay ? startDate : endDate,
    });

    console.log({ entryDate, exitDate, rangeStart, rangeEnd });

    return isDateEqual({ entryDate, exitDate, rangeStart, rangeEnd });
  });
};

/**
 * Valida si las fechas de entrada y salida son exactamente iguales a las fechas de rango de los checkoutLines.
 * @param checkoutLines - Array de objetos que contienen las fechas de rango.
 * @param startDate - Fecha de inicio en formato "día-mes-año".
 * @param endDate - Fecha de fin en formato "día-mes-año".
 * @returns Índice del checkoutLine que tiene fechas exactamente iguales, -1 si no hay coincidencias.
 */
export const validateHourEqual = ({
  checkoutLines,
  dates,
}: {
  checkoutLines: any[];
  dates: IBookingDates;
}): number => {
  return checkoutLines.findIndex((checkoutLine) => {
    const { entryDate, exitDate, rangeStart, rangeEnd } = parseDatesWithHours({
      checkoutLine,
      ...dates,
    });

    return isDateEqual({ entryDate, exitDate, rangeStart, rangeEnd });
  });
};

export const isDateEqual = ({
  entryDate,
  exitDate,
  rangeStart,
  rangeEnd,
}: {
  entryDate: Date;
  exitDate: Date;
  rangeStart: Date;
  rangeEnd: Date;
}): boolean => {
  return entryDate.getTime() === rangeStart.getTime() && exitDate.getTime() === rangeEnd.getTime();
};

export const getRangeBookingDate = () => {
  const today = new Date();

  const startDate = new Date(today);
  startDate.setDate(today.getDate() + 1);

  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 1 + DAYS_RANGE);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};

export const getDayAndHourBookingDate = () => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);
  return {
    startDate: formatDate(startDate),
    startTime: '08:00:00',
    endTime: '09:00:00',
  };
};

export const getDayBookingDate = () => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);
  return {
    startDate: formatDate(startDate),
  };
};

export const convertHourTo24Hours = (hour12: string): string => {
  // Separar la parte de hora y AM/PM
  const [hour, period] = hour12.split(' ');

  // Separar hora y minutos
  let [hours, minutes] = hour.split(':').map(Number);

  // Convertir a formato 24 horas
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  // Formatear la salida con dos dígitos
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

/**
 * Convierte una hora en formato "HH:mm:ss" a formato "h:mm AM/PM".
 * @param hour24 - Hora en formato "HH:mm:ss".
 * @returns Hora en formato "h:mm AM/PM".
 */
export const convert24HoursTo12HourFormat = (hour24: string): string => {
  let [hours, minutes] = hour24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convertir 0 a 12 para el formato de 12 horas
  return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const addOneHour = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const newHours = (hours + 1) % 24;
  return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

export const addOneHourFormat12 = (time: string): string => {
  return convert24HoursTo12HourFormat(addOneHour(time.split(' ')[0]));
};
